import { X } from 'lucide-react'
import { getCategoryById, getCategoryGroupById } from '@/utils/categories'
import type { ListingFilters } from '@/types/filters'

interface ActiveFiltersProps {
  filters: ListingFilters
  onRemoveFilter: (key: keyof ListingFilters) => void
  onClearAll: () => void
}

interface FilterChipProps {
  label: string
  onRemove: () => void
}

function FilterChip({ label, onRemove }: FilterChipProps) {
  return (
    <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
      {label}
      <button
        onClick={onRemove}
        className="p-0.5 hover:bg-primary-200 rounded-full"
        aria-label={`Remove ${label} filter`}
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  )
}

export function ActiveFilters({ filters, onRemoveFilter, onClearAll }: ActiveFiltersProps) {
  const chips: { key: keyof ListingFilters; label: string }[] = []

  if (filters.query) {
    chips.push({ key: 'query', label: `"${filters.query}"` })
  }

  if (filters.group) {
    const group = getCategoryGroupById(filters.group)
    if (group) chips.push({ key: 'group', label: group.name })
  }

  if (filters.category) {
    const category = getCategoryById(filters.category)
    if (category) chips.push({ key: 'category', label: category.name })
  }

  if (filters.location) {
    chips.push({ key: 'location', label: filters.location })
  }

  if (filters.priceMin || filters.priceMax) {
    const min = filters.priceMin ? `$${filters.priceMin}` : '$0'
    const max = filters.priceMax ? `$${filters.priceMax}` : ''
    chips.push({
      key: 'priceMin',
      label: max ? `${min} - ${max}` : `${min}+`,
    })
  }

  if (filters.condition) {
    chips.push({ key: 'condition', label: filters.condition })
  }

  if (filters.saleType) {
    chips.push({
      key: 'saleType',
      label: filters.saleType === 'auction' ? 'Auction' : 'Buy Now',
    })
  }

  if (filters.make) {
    chips.push({ key: 'make', label: filters.make })
  }

  if (filters.model) {
    chips.push({ key: 'model', label: filters.model })
  }

  if (filters.transmission) {
    chips.push({ key: 'transmission', label: filters.transmission })
  }

  if (chips.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <span className="text-sm text-slate-500">Active filters:</span>
      {chips.map((chip) => (
        <FilterChip
          key={chip.key}
          label={chip.label}
          onRemove={() => {
            onRemoveFilter(chip.key)
            // Also clear related filters
            if (chip.key === 'priceMin') onRemoveFilter('priceMax')
            if (chip.key === 'group') onRemoveFilter('category')
          }}
        />
      ))}
      <button
        onClick={onClearAll}
        className="text-sm text-slate-500 hover:text-slate-700 underline"
      >
        Clear all
      </button>
    </div>
  )
}
